import { Button } from "@dune/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../../api/queries/files.queries";
import { gfasQueryKeys } from "../../api/query-keys/gfas.query-keys";

interface AddGFADocumentButtonProps {
	gfaId: number;
	onUploadSuccess?: (fileId: string) => void;
}

export function AddGFADocumentButton({
	gfaId,
	onUploadSuccess,
}: AddGFADocumentButtonProps) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isUploading, setIsUploading] = useState(false);
	const queryClient = useQueryClient();

	const handleFileSelect = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setIsUploading(true);

		try {
			const signedUrl = await getSignedPutUrl({
				bucket: "private",
				contentType: file.type,
			});

			await uploadFileToS3(signedUrl.signedUrl, file);

			const registeredFile = await registerS3File({
				bucket: "private",
				key: signedUrl.key,
				name: file.name,
				mimeType: file.type,
				size: file.size,
			});

			onUploadSuccess?.(registeredFile.id);

			toast.success("Fichier téléchargé avec succès");

			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.filesOnGFA(gfaId.toString()),
			});
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(gfaId.toString()),
			});
		} catch (error) {
			console.error("Error uploading file:", error);
			toast.error("Erreur lors du téléchargement du fichier");
		} finally {
			setIsUploading(false);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	return (
		<div>
			<input
				ref={fileInputRef}
				type="file"
				className="hidden"
				onChange={handleFileSelect}
				disabled={isUploading}
			/>
			<Button
				type="button"
				variant="blueChip"
				size="sm"
				disabled={isUploading}
				onClick={() => fileInputRef.current?.click()}
				className="h-7 rounded-full"
			>
				<Plus className="w-3 h-3" />
				{isUploading ? "Téléchargement..." : "Ajouter"}
			</Button>
		</div>
	);
}
